import { useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { saveWidgets } from './actions/saveActions';

/**
 * SaveButton - כפתור שמירת שינויים (סטנד-אלון)
 * 
 * Business Component - מקבל dispatch ונתונים דרך props, מזמן את ה-action בעצמו
 * 
 * @param {string} dashboardId - מזהה הדשבורד
 * @param {object} dashboard - אובייקט הדשבורד המלא
 * @param {boolean} hasUnsavedChanges - האם יש שינויים שלא נשמרו
 * @param {function} dispatch - Redux dispatch function
 * @param {string} organizationId - מזהה הארגון
 * @param {array} savedWidgets - רכיבים שמורים ברמת ארגון
 * @param {boolean} fullWidth - האם הכפתור ברוחב מלא
 * @param {function} onSuccess - callback להצלחה
 * @param {function} onError - callback לשגיאה
 */
const SaveButton = ({
    dashboardId,
    dashboard,
    hasUnsavedChanges,
    dispatch,
    organizationId,
    savedWidgets = [],
    fullWidth = false,
    onSuccess,
    onError
}) => {
    const [isSaving, setIsSaving] = useState(false);

    const handleSave = async () => {
        if (!dashboardId || !hasUnsavedChanges || isSaving) return;

        try {
            setIsSaving(true);

            console.log('💾 SaveButton - Saving dashboard:', {
                dashboardId,
                dashboard,
                hasWidgets: !!dashboard?.current?.widgets,
                widgetsCount: dashboard?.current?.widgets?.length || 0,
                organizationId,
                savedWidgetsCount: savedWidgets.length
            });

            const widgets = dashboard?.current?.widgets || [];
            const settings = dashboard?.current?.settings || { bgColor: '#fcfbfa', bgImage: '' };

            await dispatch(saveWidgets(dashboardId, widgets, settings, organizationId, savedWidgets));

            console.log('✅ SaveButton - Dashboard saved successfully');
            onSuccess?.();
        } catch (error) {
            console.error('❌ SaveButton - Error saving dashboard:', error);
            onError?.(error.message || 'שגיאה בשמירת הדשבורד');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Button
            fullWidth={fullWidth}
            variant="outlined"
            startIcon={isSaving ? <CircularProgress size={16} sx={{ color: '#1e3a5f' }} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={!hasUnsavedChanges || isSaving}
            sx={{
                borderColor: 'rgba(30,58,95,0.45)',
                color: '#1e3a5f',
                fontWeight: 500,
                px: 2,
                py: 0.5,
                fontSize: '0.875rem',
                borderRadius: '12px',
                textTransform: 'none',
                minHeight: '36px',
                transition: 'all 0.2s',
                '&:hover': {
                    borderColor: '#1e3a5f',
                    bgcolor: 'rgba(30,58,95,0.05)',
                    boxShadow: '0 2px 10px rgba(30,58,95,0.15)',
                    transform: 'translateY(-1px)'
                },
                '&.Mui-disabled': {
                    borderColor: 'rgba(30,58,95,0.3)',
                    color: 'rgba(30,58,95,0.3)'
                },
                gap: 1
            }}
        >
            {isSaving ? 'שומר...' : hasUnsavedChanges ? 'שמירה' : 'שמירה'}
        </Button>
    );
};

export default SaveButton;
